import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useLocation } from 'react-router';

import BrandHeader from '../BrandHeader';
import UserBlock from '../UserBlock';
import SidebarItem from './SidebarItem';
import { useSidebarNav } from './useSidebarNav';
import { useCurrentUser } from '../../../auth/useCurrentUser';
import { useActionsDisplayMode } from '../../../auth/useActionsDisplayMode';
import { useEnv } from '../../../context/EnvContext';
import { useStoryblokMode } from '../../../auth/useStoryblokMode';
import { tenantApi } from '../../../api/tenantApi';
import { useQuery } from '@tanstack/react-query';

interface Props {
  tenantCode: string | undefined;
  tenantName: string | undefined;
  /** Optional close handler — shown on mobile drawer. */
  onClose?: () => void;
}

const Sidebar: React.FC<Props> = ({ tenantCode, tenantName, onClose }) => {
  const { isSuperAdmin, roles, rbacV2Enabled, hasPermission } = useCurrentUser();
  const bareChrome = rbacV2Enabled && hasPermission('hide_chrome');
  const { data: allTenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantApi.getAll(),
    enabled: bareChrome,
  });
  // For bare-chrome users with only one accessible tenant, hide the "Change" affordance.
  const hideChange = bareChrome && (allTenants?.length ?? 2) <= 1;
  // Task V2 tenants hide Adventures (and other `hideWhenTaskV2` items).
  const { taskV2Enabled, hideV1Actions } = useActionsDisplayMode(tenantCode || '');
  // Build in Flex follows this — the app builder is deployed per environment.
  const { currentEnv } = useEnv();
  // CPT-1219: the tenant's Storyblok attribute is the sole gate on Content vs CMS.
  const { storyblokOnly } = useStoryblokMode(tenantCode || '');
  const items = useSidebarNav({
    tenantCode: tenantCode || '',
    // Gate admin-only sidebar groups on the current user's super-admin role.
    // (Replaces the retired demo-mode admin-functions flag — see F23.6.)
    includeAdmin: isSuperAdmin,
    // Additive capability roles (e.g. `flex`) reveal their own `requiredRole`
    // group without granting full admin. Flag-off gate only.
    roles,
    // Gate `masterOnly` items (e.g. the action Library) on the master tenant.
    isMaster: tenantName === 'MasterTenant',
    // Hide `hideWhenTaskV2` items (e.g. Adventures) when the tenant is on Task V2.
    taskV2Enabled,
    storyblokOnly,
    currentEnv,
    // Hides the V1 Actions group and renames Task V2 to plain "Actions".
    hideV1Actions,
    // RBAC_V2 — switches group gating from `requiredRole` to `SECTION_GATES`.
    rbacV2Enabled,
    hasPermission,
  });

  // Auto-open groups whose children contain the active route; user can
  // override via manual toggle. Manual overrides reset on route change so
  // the active group is always visible after navigation.
  const { pathname } = useLocation();
  const [manualToggle, setManualToggle] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setManualToggle({});
  }, [pathname]);

  // CPT-1375 put FAQs/Cards/Agreements under Flex Components as well as
  // Content, so one path now has two sidebar entries. "Is this the active
  // page?" stops being answerable from the pathname: both entries match, both
  // groups open, and both light up. Exactly one group owns the highlight, and
  // the rule is "the one you came through" — clicking Agreements under Flex
  // Components keeps Flex Components lit, rather than throwing the user back to
  // Content. Falls back to the route's primary group for a deep link or a
  // refresh, where there is no "came through".
  const [groupByPath, setGroupByPath] = useState<Record<string, string>>({});
  const matches = (p: string): boolean => pathname === p || pathname.startsWith(`${p}/`);

  const activeGroupKey = React.useMemo(() => {
    type Hit = { groupKey: string; path: string; isPrimary: boolean };
    const hits: Hit[] = [];
    for (const item of items) {
      if (item.type !== 'expandable') continue;
      for (const c of item.children) {
        if (matches(c.path)) hits.push({ groupKey: item.key, path: c.path, isPrimary: c.isPrimary !== false });
      }
    }
    if (hits.length === 0) return null;
    // Most specific path wins first — a sibling whose path is a prefix of the
    // active one must not claim it (the rule SidebarItem already applies).
    const deepest = Math.max(...hits.map((h) => h.path.length));
    const finalists = hits.filter((h) => h.path.length === deepest);
    const chosen = finalists.find((h) => groupByPath[h.path] === h.groupKey)
      ?? finalists.find((h) => h.isPrimary)
      ?? finalists[0];
    return chosen.groupKey;
  }, [items, pathname, groupByPath]);

  const isOpen = (key: string, childPaths: string[]): boolean => {
    if (manualToggle[key] !== undefined) return manualToggle[key];
    // Only the owning group auto-opens. The other one is still there to open by
    // hand; it just does not claim the page.
    return key === activeGroupKey && childPaths.some(matches);
  };

  const toggle = (key: string, childPaths: string[]) => {
    setManualToggle((prev) => ({ ...prev, [key]: !isOpen(key, childPaths) }));
  };

  return (
    <Box component="nav" aria-label="Primary" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <BrandHeader tenantName={tenantName} tenantCode={tenantCode} onClose={onClose} hideChange={hideChange} />

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {items.map((item) => {
          const childPaths = item.type === 'expandable' ? item.children.map((c) => c.path) : [];
          return (
            <SidebarItem
              key={item.key}
              item={item}
              open={item.type === 'expandable' ? isOpen(item.key, childPaths) : false}
              onToggle={() => toggle(item.key, childPaths)}
              isActiveGroup={item.key === activeGroupKey}
              onChildNavigate={(path) => setGroupByPath((prev) => ({ ...prev, [path]: item.key }))}
            />
          );
        })}
      </Box>

      <UserBlock />
    </Box>
  );
};

export default Sidebar;
